import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';

// Constants
import { DropdownPlacement } from '../dropdown/WpDropdownMenu';

// Components
import WpTextField from '../text-field/WpTextField';
import WpCalendarPicker from '../calendar-input/WpCalendarPicker';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from '../dropdown';

export interface Props {
  /** ID for the input */
  id: string;
  /** Label for the input */
  label: string;
  /** Hide the label, but keep on screen for screen readers */
  labelHidden?: boolean;
  /** Hint text to display */
  placeholder?: string;
  /** Disable the input */
  disabled?: boolean;
  /** Additional hint text to display */
  helpText?: React.ReactNode;
  /** Callback when value is changed */
  onChange?(value: string): void;
  /** Callback when input is focused */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  /** Initial value for the input - Unix timestamp*/
  value?: string;
  required?: boolean;
  /** Error to display beneath the label */
  error?: string;
  /** Automatically focus the input */
  autoFocus?: boolean;
  returnMoment?: boolean;
  showRequiredIndicator?: boolean;
  placement?: DropdownPlacement;
  isOutsideRange?: Function;
}

const WpDateInput: React.FC<Props> = ({
  id,
  label,
  labelHidden,
  disabled,
  helpText,
  onChange,
  onFocus,
  onBlur,
  value,
  required,
  error,
  autoFocus,
  placeholder = '',
  returnMoment = false,
  showRequiredIndicator = true,
  placement,
  isOutsideRange,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState(null);

  // This is a workaround to close the dropdown after selecting a date
  // this logic / functionality is kinda wacky - maybe worth revisiting in the future (josh)
  const fakeClick = () => {
    // need to check if item is in the mobile drawer - if so click that backdrop
    const backdropElement: HTMLElement = document.querySelector(
      '.WpDrawer.WpDrawer--open > .WpBackdrop.WpBackdrop--open'
    ) as HTMLElement;
    if (backdropElement) backdropElement.click();
    else {
      const el = document.createElement('div');
      document.body.appendChild(el);
      el.click();
      document.body.removeChild(el);
    }
  };

  const classNamePrefix = 'WpDateInput';
  const classes = classnames(classNamePrefix);

  const handleDateChange = value => {
    if (onChange) {
      if (returnMoment) {
        onChange(value);
      } else {
        onChange(value.unix());
      }
    }
    setSelectedValue(value);
    fakeClick();
  };

  const getMoment = value => {
    if (!value) return null;
    return moment.unix(value);
  };

  const getDisplayValue = () => {
    if (!selectedValue) return '';
    return selectedValue.format('L');
  };

  useEffect(() => {
    if (value) setSelectedValue(getMoment(value));
    else setSelectedValue(null);
  }, [value]);

  return (
    <div className={classes}>
      <WpDropdown placement={placement} disabled={disabled}>
        <WpDropdownToggle>
          <WpTextField
            id={id}
            label={label}
            labelHidden={labelHidden}
            disabled={disabled}
            placeholder={placeholder}
            error={error}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            readOnly
            required={required}
            helpText={helpText}
            value={getDisplayValue()}
            prefixIcon="calendar-alt"
            showRequiredIndicator={showRequiredIndicator}
          />
        </WpDropdownToggle>
        <WpDropdownMenu>
          <WpDropdownMenuItem removeBackgroundHover>
            <WpCalendarPicker
              onDateChange={handleDateChange}
              selectedDay={selectedValue}
              isOutsideRange={isOutsideRange}
            />
          </WpDropdownMenuItem>
        </WpDropdownMenu>
      </WpDropdown>
    </div>
  );
};

export default WpDateInput;
